import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import config from 'devextreme/core/config';
import CustomStore from 'devextreme/data/custom_store';
import notify from 'devextreme/ui/notify';
import repaintFloatingActionButton from 'devextreme/ui/speed_dial_action/repaint_floating_action_button';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FusionAuthService } from '../../../../../shared/services/fusion-auth';
import { DateHelperService, PWADetectorService, ScreenService } from '../../../../../shared/services/general';
import { StandingOrderService } from '../../../../../shared/services/ocl';

@Component({
    selector: 'app-standing-order-search-content',
    templateUrl: './standing-order-search-content.component.html',
    styleUrls: ['./standing-order-search-content.component.scss'],
})
export class StandingOrderSearchContentComponent implements OnInit, OnDestroy {
    @Input() public filtersSubmitted: EventEmitter<any> = new EventEmitter<any>();
    @Output() public refreshed: EventEmitter<any> = new EventEmitter<any>();
    public filters: any;
    public patientsStore: CustomStore | [] = [];
    public selectedRowKeys: number[] = [];
    public role: string;
    public isLoading = false;
    public isPhoneOrTablet: boolean = false;
    public isPhone: boolean = false;
    private onDestroy$: Subject<void> = new Subject<void>();

    constructor(private router: Router,
                private pwaDetectorService: PWADetectorService, private standingOrderService: StandingOrderService, public dateHelper: DateHelperService,
                private fusionAuthService: FusionAuthService, private screen: ScreenService, private renderer: Renderer2) {
        this.role = fusionAuthService.Profile.registrations[0].roles[0];
    }

    public ngOnInit() {
        this.filtersSubmitted.pipe(takeUntil(this.onDestroy$)).subscribe(filters => {
            this.filters = filters;
            this.setFilters(this.filters);
        });

        this.responsiveValues();
        this.screen.changed.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
            this.responsiveValues();
        });
    }

    private responsiveValues() {
        this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
        this.isPhone = this.screen.sizes['screen-x-small'];

        const offset = this.isPhone ? '-15 75' : '-25 75';
        config({
            floatingActionButtonConfig: {
                icon: 'rowfield',
                shading: true,
                direction: 'down',
                position: {
                    of: 'body',
                    my: 'right top',
                    at: 'right top',
                    offset,
                },
            },
        });
        repaintFloatingActionButton();
    }

    public onButtonReady(e: any) {
        const parent = e.actionElement[0];
        const labelButtons = parent.querySelector('.dx-overlay-content');
        if (labelButtons !== undefined) {
            this.renderer.setStyle(labelButtons, 'overflow', 'visible');
        }
    }

    public editPatient(patient: any) {
        if (this.pwaDetectorService.isPWA()) {
            this.router.navigate(['secured/patients/form', {
                id: patient.id, redirectToPath: this.router.url,
            }]);
        } else {
            const url = this.router.serializeUrl(
                this.router.createUrlTree(['secured/patients/form', {
                    id: patient.id,
                    redirectToPath: this.router.url,
                }]),
            );
            window.open(url, '_blank');
            window.close();
        }
    }

    public newStandingOrder(patientId?: number) {
        if (this.pwaDetectorService.isPWA()) {
            this.router.navigate(['secured/standing-orders/form'], {
                queryParams: { patientId },
            });
        } else {
            let url: string;
            if (patientId) {
                url = this.router.serializeUrl(
                    this.router.createUrlTree(['secured/standing-orders/form', {
                        patientId,
                    }]),
                );
            } else {
                url = this.router.serializeUrl(
                    this.router.createUrlTree(['secured/standing-orders/form']),
                );
            }
            window.open(url, '_blank');
        }
    }

    public editStandingOrder(id: number) {
        if (this.pwaDetectorService.isPWA()) {
            this.router.navigate(['secured/standing-orders/form'], {
                queryParams: { id },
            });
        } else {
            const url = this.router.serializeUrl(
                this.router.createUrlTree(['secured/standing-orders/form', {
                    id,
                }]),
            );
            window.open(url, '_blank');
        }
    }

    public cancelStandingOrder(ids: number[]) {
        this.isLoading = true;
        this.standingOrderService.cancel(ids).pipe(takeUntil(this.onDestroy$)).subscribe(() => {
            this.isLoading = false;
            notify('The selected Standing Order(s) has been cancelled', 'success', 3000);
            this.setFilters(this.filters);
        });
    }

    public setFilters(filters: any) {
        const user = this.fusionAuthService.Profile;
        switch (this.role) {
            case 'Client Admin':
            case 'Client Basic':
                const clientIds = user ? user.data.clients : this.fusionAuthService.Profile.data.clients;
                this.patientsStore = this.standingOrderService.getExtremeStore(filters, clientIds);
                break;
            default:
                this.patientsStore = this.standingOrderService.getExtremeStore(filters);
        }
    }

    public onRowExpanding(event: any) {
        if (this.isPhoneOrTablet) {
            event.component.collapseAll(-1);
        }
    }

    public ngOnDestroy(): void {
        this.onDestroy$.next(); this.onDestroy$.complete()
    }
}
